/**
 * API functions for Departments
 * POST /departments/list/ - list departments (empty body)
 * POST /departments/list - list departments (query params: limit, cursor, isActive)
 * GET /departments/facilities/:facilityId/departments - list by facility (query params: limit, cursor, isEnabled)
 * GET /departments/search?name=... - search departments by name
 */

import { apiClient } from './auth';
import { fetchAllListPages } from './cursorPagination';
import type {
  GetDepartmentsListResponse,
  GetDepartmentsListParams,
  GetDepartmentsListPaginatedResponse,
  CreateDepartmentPayload,
  UpdateDepartmentPayload,
  DepartmentItem,
} from '../Interfaces/DepartmentInterface';

/** Params for facility-filtered list */
export interface GetDepartmentsByFacilityParams {
  limit: number;
  isActive?: boolean;
  cursor?: string;
}

/** Raw item from GET /departments/facilities/:id/departments */
interface FacilityDepartmentRow {
  _id?: string;
  facilityId?: string;
  departmentId?: string;
  isEnabled?: boolean;
  department?: {
    _id: string;
    name: string;
    code?: string;
    specialty?: string;
    isActive?: boolean;
  };
}

/**
 * Search departments by name.
 * GET /departments/search?name=... (name is dynamic). Backend requires at least 2 characters.
 */
export interface SearchDepartmentsParams {
  name: string;
}

export interface SearchDepartmentsResponse {
  departments: Array<{ id: string; name: string; code?: string; isActive?: boolean }>;
  total: number;
}

export async function searchDepartmentsApi(
  params: SearchDepartmentsParams
): Promise<SearchDepartmentsResponse> {
  const searchParams = new URLSearchParams();
  if (params.name?.trim()) searchParams.set("name", params.name.trim());
  const url = `/departments/search?${searchParams.toString()}`;
  const response = await apiClient.get<SearchDepartmentsResponse>(url);
  return response.data;
}

/**
 * Agent-accessible department lookup (read-only).
 * GET /departments/lookup — requires OPPORTUNITIES:VIEW_DETAILS or PATIENTS_CONTACTS:VIEW_DETAILS.
 */
export async function getDepartmentsLookupApi(
  params: GetDepartmentsListParams
): Promise<GetDepartmentsListPaginatedResponse> {
  const limit = Math.min(params.limit ?? 100, 200);
  const searchParams = new URLSearchParams({
    limit: String(limit),
    isActive: String(params.isActive ?? true),
  });
  if (params.cursor) {
    searchParams.set("cursor", params.cursor);
  }
  const response = await apiClient.get<GetDepartmentsListPaginatedResponse>(
    `/departments/lookup?${searchParams.toString()}`
  );
  return response.data;
}

/**
 * Fetch departments list. Used to populate department dropdown when creating an opportunity.
 * Loads all pages via POST /departments/list cursor pagination.
 */
export async function getDepartmentsListApi(): Promise<GetDepartmentsListResponse> {
  const items = await fetchAllDepartmentsList({ limit: 200, isActive: true });
  return { items, nextCursor: null };
}

/**
 * Fetch departments list. Backend reads from query params: limit, cursor, isActive.
 * Page maximum is 200 (backend allows up to 200).
 */
export async function getDepartmentsListPaginatedApi(
  params: GetDepartmentsListParams
): Promise<GetDepartmentsListPaginatedResponse> {
  const limit = Math.min(params.limit ?? 100, 200);
  const searchParams = new URLSearchParams({
    limit: String(limit),
    isActive: String(params.isActive ?? true),
  });
  if (params.cursor) {
    searchParams.set('cursor', params.cursor);
  }
  const url = `/departments/list?${searchParams.toString()}`;
  const response = await apiClient.post<Record<string, never>, GetDepartmentsListPaginatedResponse>(
    url,
    {}
  );
  return response.data;
}

/**
 * Fetch all departments across cursor pages. POST /departments/list
 */
export async function fetchAllDepartmentsList(
  params: Omit<GetDepartmentsListParams, 'cursor'>
): Promise<DepartmentItem[]> {
  const limit = Math.min(params.limit ?? 200, 200);
  return fetchAllListPages((cursor) =>
    getDepartmentsListPaginatedApi({ ...params, limit, cursor })
  );
}

/**
 * Fetch all departments for a facility (e.g. logged-in facility).
 * GET /departments/facilities/:facilityId/departments?limit=50&isEnabled=true
 */
export async function getDepartmentsByFacilityIdApi(
  facilityId: string
): Promise<GetDepartmentsListPaginatedResponse> {
  return getDepartmentsListByFacilityApi(facilityId, {
    limit: 50,
    isActive: true,
  });
}

/**
 * Agent-accessible facility department lookup (read-only).
 * GET /departments/facilities/:facilityId/departments/lookup
 * Requires OPPORTUNITIES:VIEW_DETAILS or PATIENTS_CONTACTS:VIEW_DETAILS.
 */
export async function getDepartmentsListByFacilityLookupApi(
  facilityId: string,
  params: GetDepartmentsByFacilityParams
): Promise<GetDepartmentsListPaginatedResponse> {
  const limit = params.limit ?? 200;
  const searchParams = new URLSearchParams({
    limit: String(limit),
    isEnabled: String(params.isActive ?? true),
  });
  if (params.cursor) {
    searchParams.set('cursor', params.cursor);
  }
  const url = `/departments/facilities/${facilityId}/departments/lookup?${searchParams.toString()}`;
  const response = await apiClient.get<{ items: FacilityDepartmentRow[]; nextCursor?: string }>(url);
  const rows = response.data.items ?? [];
  const items: DepartmentItem[] = rows.map((row) => {
    const d = row.department;
    if (!d) {
      return {
        _id: row.departmentId ?? row._id ?? '',
        name: '',
        code: undefined,
        isActive: row.isEnabled,
      };
    }
    return {
      _id: d._id,
      name: d.name,
      code: d.code,
      isActive: d.isActive ?? row.isEnabled,
    };
  });
  return {
    items,
    nextCursor: response.data.nextCursor ?? null,
  };
}

/**
 * Fetch all departments for a facility via agent lookup endpoint.
 */
export async function fetchAllDepartmentsByFacilityLookupApi(
  facilityId: string,
  params: Omit<GetDepartmentsByFacilityParams, 'cursor'>
): Promise<DepartmentItem[]> {
  const limit = params.limit ?? 200;
  return fetchAllListPages((cursor) =>
    getDepartmentsListByFacilityLookupApi(facilityId, { ...params, limit, cursor })
  );
}

/**
 * Fetch departments for a specific facility.
 * GET /departments/facilities/:facilityId/departments with query params: limit, cursor, isEnabled.
 */
export async function getDepartmentsListByFacilityApi(
  facilityId: string,
  params: GetDepartmentsByFacilityParams
): Promise<GetDepartmentsListPaginatedResponse> {
  const limit = params.limit ?? 200;
  const searchParams = new URLSearchParams({
    limit: String(limit),
    isEnabled: String(params.isActive ?? true),
  });
  if (params.cursor) {
    searchParams.set('cursor', params.cursor);
  }
  const url = `/departments/facilities/${facilityId}/departments?${searchParams.toString()}`;
  const response = await apiClient.get<{ items: FacilityDepartmentRow[]; nextCursor?: string }>(url);
  const rows = response.data.items ?? [];
  const items: DepartmentItem[] = rows.map((row) => {
    const d = row.department;
    if (!d) {
      return {
        _id: row.departmentId ?? row._id ?? '',
        name: '',
        code: undefined,
        isActive: row.isEnabled,
      };
    }
    return {
      _id: d._id,
      name: d.name,
      code: d.code,
      isActive: d.isActive ?? row.isEnabled,
    };
  });
  return {
    items,
    nextCursor: response.data.nextCursor ?? null,
  };
}

/**
 * Fetch all departments for a facility across cursor pages.
 */
export async function fetchAllDepartmentsByFacilityApi(
  facilityId: string,
  params: Omit<GetDepartmentsByFacilityParams, 'cursor'>
): Promise<DepartmentItem[]> {
  const limit = params.limit ?? 200;
  return fetchAllListPages((cursor) =>
    getDepartmentsListByFacilityApi(facilityId, { ...params, limit, cursor })
  );
}

/**
 * Update a department.
 * PATCH /departments/update/:departmentId with body { name?, code? }.
 */
export async function updateDepartmentApi(
  departmentId: string,
  payload: UpdateDepartmentPayload
): Promise<DepartmentItem> {
  const response = await apiClient.patch<UpdateDepartmentPayload, DepartmentItem>(
    `/departments/update/${departmentId}`,
    payload
  );
  return response.data;
}

/**
 * Create a new department.
 * POST /departments with body { name, code, isActive, facilityIds }.
 */
export async function createDepartmentApi(
  payload: CreateDepartmentPayload
): Promise<DepartmentItem> {
  const response = await apiClient.post<CreateDepartmentPayload, DepartmentItem>(
    '/departments',
    {
      name: payload.name,
      code: payload.code,
      isActive: payload.isActive ?? true,
      facilityIds: payload.facilityIds ?? [],
    }
  );
  return response.data;
}
